import React, { useEffect, useState } from "react";
import { Table, Input, message } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "react-select";
import { getMISheaders, getMISApplicationsDetails } from "../../auth/services/privateapicalling";
import { OverlayTrigger, Tooltip } from "react-bootstrap";



const MISMyApplication = () => {
  const [applicationData, setApplicationData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const status = "all";
  const location = useLocation();


  
  const language = useSelector((state: any) => state.auth.language);
  const token = useSelector((state: any) => state.auth.token);
  const navigate = useNavigate();
  
  const [apiHeaderResponse, setApiHeaderResponse] = useState([]);
  const [selectedHeader, setSelectedHeader] = useState<any>(null);

  // Fetch MIS headers on component mount
  useEffect(() => {
    const fetchHeaders = async () => {
      try {
        const response = await getMISheaders(token);
       
        
        if (response?.success && response?.data) {
          setApiHeaderResponse(response.data);
        }

        if (location.state?.selectedHeader) {
          const preSelectedHeader = location.state.selectedHeader;
          setSelectedHeader(preSelectedHeader);
        }
      } catch (error) {
       
        message.error("Failed to load report types");
      }
    };
    
    fetchHeaders();
  }, [token, location.state]);

  // Fetch applications when header, page, or limit changes
  useEffect(() => {
    if (selectedHeader) {
      fetchApplications();
    }
  }, [selectedHeader, currentPage, limit]);

  const fetchApplications = async () => {
    if (!selectedHeader) {
      message.warning("Please select a report type");
      return;
    }

    setLoading(true);
    try {
      const response = await getMISApplicationsDetails(
        selectedHeader.value,
        limit,
        currentPage,
        status,
        token
      );
     
      
      if (response?.success && response?.data) {
        // Map the API response to table format
        const formattedData = response.data.map((item: any, index: number) => ({
          key: item.applicationId || index,
          applicationId: item.applicationId,
          applicantHeader: selectedHeader.label,
          applicantYear: item.year,
          apmc: item.apmc || item.apmcName,
          appliedDate: item.createdAt,
          status: item.status,
          action: item.action || false,
        }));
        
        setApplicationData(formattedData);
        setTotalItems(response.totalCount || 0);
      }
    } catch (error) {
      
      message.error("Failed to load applications");
      setApplicationData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (!value) {
      // If search is cleared, fetch all data again
      if (selectedHeader) {
        fetchApplications();
      }
      return;
    }

    // Filter locally
    const filtered = applicationData.filter((item) =>
      item.applicationId.toLowerCase().includes(value.toLowerCase())
    );
    setApplicationData(filtered);
  };

  // Select options for header dropdown
  const selectOptions = apiHeaderResponse.map((item: any) => ({
    value: item.id,
    label: item.header_name,
  }));

  // Handle header selection change
  const handleHeaderChange = (selectedOption: any) => {
    setSelectedHeader(selectedOption);
    setCurrentPage(1); // Reset to first page when changing header
    setApplicationData([]); // Clear previous data
  };

  const columns = [
    {
      title: language.application_id,
      dataIndex: "applicationId",
      render: (text: string) => (
        <h6 className="fw-medium">
          <Link to="#">{text}</Link>
        </h6>
      ),
    },
    { title: "Report", dataIndex: "applicantHeader",
      render: (text: string) => (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id={`tooltip-scheme-${text}`}>
              {text}
            </Tooltip>
          }
        >
          <div
            style={{
              maxWidth: 200,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: "pointer",
            }}
          >
            {text}
          </div>
        </OverlayTrigger>
      ),
     },
    { title: "Year", dataIndex: "applicantYear" },
    { title: language.apmc, dataIndex: "apmc" },
    {
      title: language.applied_date,
      dataIndex: "appliedDate",
      render: (date: string) => new Date(date).toLocaleDateString("en-GB"),
    },
    {
      title: language.application_status,
      dataIndex: "status",
      render: (status: string) => {
        const statusMapping: Record<string, string> = {
          permanentReject: "Permanent Reject",
          approvedUnsigned: "Approved",
          approve: "Approved",
          reject: "Raised Query",
          pending: "Review under Department",
          resubmission: "Resubmission",
          draft: "Draft",
        };

        return <span>{statusMapping[status] || status}</span>;
      },
    },
    {
      title: language.action,
      dataIndex: "applicationId",
      render: (text: string, record: any) =>
   
          <h6 className="fw-medium">
            <button
              className="bg-primary rounded text-white px-4 py-2"
              onClick={() =>
                navigate(record?.status==="resubmission"?"/mis-raise-query":"/mis-preview", {
                  state: { applicationId: record.applicationId , headerId: selectedHeader.value, headerName: selectedHeader.label},
                })
              }
            >
              {language.view}
            </button>
          </h6>
        
    },
  ];

  return (
    <div className="page-wrapper">
      <h2 className="px-4 pt-4">MIS My Applications</h2>
      <div className="pb-4 px-4">
        <div className="table-top-data">
          <div className="row p-3">
            <div className="d-flex gap-3 justify-content-end">
              <Select
                options={selectOptions}
                value={selectedHeader}
                onChange={handleHeaderChange}
                placeholder="Select Report"
                isLoading={apiHeaderResponse.length === 0}
                isDisabled={loading}
                className="w-25"
              />

              <Input
                placeholder="Search Application Id..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 250 }}
                allowClear
                disabled={!selectedHeader || loading}
              />
            </div>
          </div>
        </div>

        <Table
          className="table datanew dataTable no-footer"
          columns={columns}
          rowHoverable={false}
           rowKey={"applicationId"}
          dataSource={applicationData}
          loading={loading}
          pagination={{
            locale: { items_per_page: "" },
            nextIcon: <i className="ti ti-chevron-right" />,
            prevIcon: <i className="ti ti-chevron-left" />,
            pageSize: limit,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "15","20"],
            current: currentPage,
            total: totalItems,
            showTotal: (total, range) =>
              `Showing ${range[0]} - ${range[1]} of ${total} entries`,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setLimit(pageSize);
            },
          }}
        />
      </div>
    </div>
  );
};

export default MISMyApplication;